import React, { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FileExportIcon } from "@components/icons";
import { Button, Dropdown } from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import TreeDetailPDF from "./TreeDetailPDF";
import Spinner from "@components/Spinner/Spinner";

import { getTree } from "@modules/Trees/services/treeCrud";

export async function loadTreesWithImages(treeIds: number[]) {
  const results = await Promise.all(
    treeIds.map(async (id) => {
      try {
        const tree = await getTree(id);
        return { ...tree };
      } catch (err) {
        console.error(`Failed to load tree ${id}`, err);
        return null;
      }
    })
  );

  // Remove any failed trees
  return results.filter((t) => t !== null);
}

type TreeWithImages = {
  id: number;
  treeImages?: any[];
  leafImages?: any[];
  [key: string]: any;
};

const TreePDFExportPage = ({
  title,
  trees,
  renderMode = "button",
}: {
  title: string;
  trees: TreeWithImages[];
  renderMode?: "button" | "menu";
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [exportingTree, setExportingTree] = useState<TreeWithImages | null>(null);

  const handleExport = useCallback(async () => {
    if (trees.length === 0) {
      console.warn("No trees to export");
      return;
    }

    setIsLoading(true);

    try {
      // Load full tree data with images
      const treeIds = trees.map((t) => t.id);
      const fullTrees = await loadTreesWithImages(treeIds);

      if (fullTrees.length === 0) {
        console.error("Failed to load any trees");
        setIsLoading(false);
        return;
      }

      const pdf = new jsPDF("p", "mm", "a4");
      let pagesAdded = 0;

      // Process one tree at a time
      for (let i = 0; i < fullTrees.length; i++) {
        const tree = fullTrees[i];

        // Set current tree to render
        setExportingTree(tree);

        // Wait for React to render and for content to load
        await new Promise((resolve) => setTimeout(resolve, 1500));

        const el = containerRef.current;
        if (!el) {
          console.warn(`Skipping tree ${i}: container ref is null`);
          continue;
        }

        // Check element dimensions before capture
        const rect = el.getBoundingClientRect();
        console.log(`Tree ${i} dimensions:`, rect.width, rect.height, el.scrollHeight);

        if (rect.width === 0 || (rect.height === 0 && el.scrollHeight === 0)) {
          console.warn(`Skipping tree ${i}: element has zero dimensions`);
          continue;
        }

        try {
          const canvas = await html2canvas(el, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: true,
            width: 794,
            height: Math.max(el.scrollHeight, rect.height, 800),
            windowWidth: 794,
            windowHeight: Math.max(el.scrollHeight, rect.height, 800),
          });

          // Validate canvas has valid dimensions
          if (canvas.width === 0 || canvas.height === 0) {
            console.warn(`Skipping tree ${i}: canvas has zero dimensions`);
            continue;
          }

          const imgData = canvas.toDataURL("image/png");

          // Validate the image data is not empty or invalid
          if (!imgData || imgData === "data:," || imgData.length < 100) {
            console.warn(`Skipping tree ${i}: invalid image data`);
            continue;
          }

          const imgProps = pdf.getImageProperties(imgData);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

          if (pagesAdded > 0) pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
          pagesAdded++;
        } catch (err) {
          console.error(`Error processing tree ${i} for PDF:`, err);
        }
      }

      if (pagesAdded > 0) {
        pdf.save(`${title}.pdf`);
      } else {
        console.error("No valid pages to export");
      }
    } catch (err) {
      console.error("Error during PDF export:", err);
    } finally {
      setIsLoading(false);
      setExportingTree(null);
    }
  }, [trees, title]);

  const { t } = useTranslation();
  const exportLabel = isLoading ? t("downloading") : t("download_tree_details");

  // Render container for PDF capture - positioned off-screen but still in document flow
  const renderContainer = exportingTree
    ? createPortal(
        <div
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            width: "794px",
            height: "auto",
            minHeight: "800px",
            background: "white",
            zIndex: -9999,
            overflow: "visible",
            opacity: 0,
            pointerEvents: "none",
          }}
        >
          <div
            ref={containerRef}
            style={{
              width: "794px",
              minHeight: "800px",
              background: "white",
            }}
          >
            <TreeDetailPDF
              tree={exportingTree}
              treeImages={exportingTree.treeImages}
              leafImages={exportingTree.leafImages}
            />
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      {trees.length > 0 && renderMode === "button" && (
        <Button onClick={handleExport} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner height={"5px"} onRight={true}>
                {" "}
                {t("downloading")}
              </Spinner>
            </>
          ) : (
            <>
              <FileExportIcon /> {t("download_tree_details")}
            </>
          )}
        </Button>
      )}
      {trees.length > 0 && renderMode === "menu" && (
        <Dropdown.Item onClick={handleExport} disabled={isLoading}>
          <FileExportIcon /> {exportLabel}
        </Dropdown.Item>
      )}
      {renderContainer}
    </>
  );
};

export default TreePDFExportPage;

export const waitForLeafletTilesOld = (container: HTMLElement): Promise<void> => {
  return new Promise<void>((resolve) => {
    const tileImages = Array.from(
      container.querySelectorAll("img.leaflet-tile")
    ) as HTMLImageElement[];

    if (tileImages.length === 0) return resolve(); // fallback

    let loaded = 0;

    tileImages.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        loaded++;
        if (loaded === tileImages.length) resolve();
      } else {
        img.addEventListener("load", () => {
          loaded++;
          if (loaded === tileImages.length) resolve();
        });
        img.addEventListener("error", () => {
          loaded++;
          if (loaded === tileImages.length) resolve();
        });
      }
    });
  });
};

const waitForLeafletTiles = (mapElement: HTMLElement): Promise<void> => {
  return new Promise<void>((resolve) => {
    const maps = mapElement.querySelectorAll(".leaflet-map");
    let loadedMaps = 0;

    if (maps.length === 0) {
      resolve();
      return;
    }

    maps.forEach((map) => {
      const leafletMap = (window as any).L.map(map);
      leafletMap.whenReady(() => {
        leafletMap.on("load", () => {
          loadedMaps++;
          if (loadedMaps === maps.length) {
            resolve();
          }
        });

        // Fallback in case the load event doesn't fire
        setTimeout(() => {
          loadedMaps++;
          if (loadedMaps === maps.length) {
            resolve();
          }
        }, 5000);
      });
    });
  });
};
